/**
 * Status-to-Message Matrix
 * Defines which ESS messages can be triggered based on loan application state
 * Based on ESS API Documentation v1.0.11
 */

// Loan states as defined in ESS API
export const LOAN_STATES = {
  INITIATED: 'Initiated',
  LOAN_OFFER_AT_FSP: 'Loan offer at FSP',
  FSP_REJECTED: 'FSP Rejected',
  LOAN_OFFER_AT_EMPLOYEE: 'Loan Offer at employee',
  EMPLOYEE_REJECTED: 'Employee Rejected',
  PENDING_FOR_APPROVAL: 'Pending for approval',
  EMPLOYEE_CANCELED: 'Employee canceled',
  EMPLOYER_REJECTED: 'Employer Rejected',
  SUBMITTED_FOR_DISBURSEMENT: 'Submitted for disbursement',
  FSP_CANCELED: 'FSP Canceled',
  COMPLETED: 'Completed',
  WAITING_FOR_LIQUIDATION: 'Waiting for liquidation',
  DISBURSEMENT_FAILURE: 'Disbursement Failure',
  ACTIVE: 'Active',
  DEFAULTED: 'Defaulted',
  // Additional common states
  DISBURSED: 'Disbursed',
  OVERDUE: 'Overdue',
  APPROVED: 'Approved',
  PENDING: 'Pending'
};

// State descriptions for UI
export const STATE_DESCRIPTIONS = {
  [LOAN_STATES.INITIATED]: 'Created but not submitted',
  [LOAN_STATES.LOAN_OFFER_AT_FSP]: 'Loan submitted to FSP',
  [LOAN_STATES.FSP_REJECTED]: 'Loan closed by FSP',
  [LOAN_STATES.LOAN_OFFER_AT_EMPLOYEE]: 'FSP responded with offer',
  [LOAN_STATES.EMPLOYEE_REJECTED]: 'Loan closed by employee',
  [LOAN_STATES.PENDING_FOR_APPROVAL]: 'Employee accepted, waiting employer',
  [LOAN_STATES.EMPLOYEE_CANCELED]: 'Canceled by employee',
  [LOAN_STATES.EMPLOYER_REJECTED]: 'Rejected by employer',
  [LOAN_STATES.SUBMITTED_FOR_DISBURSEMENT]: 'Approved, waiting disbursement',
  [LOAN_STATES.FSP_CANCELED]: 'Canceled by FSP',
  [LOAN_STATES.COMPLETED]: 'Loan fully paid',
  [LOAN_STATES.WAITING_FOR_LIQUIDATION]: 'For takeover flows',
  [LOAN_STATES.DISBURSEMENT_FAILURE]: 'Disbursement failed',
  [LOAN_STATES.ACTIVE]: 'Loan disbursed, repayments ongoing',
  [LOAN_STATES.DEFAULTED]: 'Loan in default',
  [LOAN_STATES.DISBURSED]: 'Loan disbursed, repayments ongoing',
  [LOAN_STATES.OVERDUE]: 'Payment overdue',
  [LOAN_STATES.APPROVED]: 'Loan approved by employer',
  [LOAN_STATES.PENDING]: 'Application pending'
};

// State owners
export const STATE_OWNERS = {
  [LOAN_STATES.INITIATED]: 'Employee',
  [LOAN_STATES.LOAN_OFFER_AT_FSP]: 'FSP',
  [LOAN_STATES.FSP_REJECTED]: null,
  [LOAN_STATES.LOAN_OFFER_AT_EMPLOYEE]: 'Employee',
  [LOAN_STATES.EMPLOYEE_REJECTED]: null,
  [LOAN_STATES.PENDING_FOR_APPROVAL]: 'Employer',
  [LOAN_STATES.EMPLOYEE_CANCELED]: null,
  [LOAN_STATES.EMPLOYER_REJECTED]: null,
  [LOAN_STATES.SUBMITTED_FOR_DISBURSEMENT]: 'FSP',
  [LOAN_STATES.FSP_CANCELED]: null,
  [LOAN_STATES.COMPLETED]: null,
  [LOAN_STATES.WAITING_FOR_LIQUIDATION]: 'FSP',
  [LOAN_STATES.DISBURSEMENT_FAILURE]: 'FSP',
  [LOAN_STATES.ACTIVE]: 'FSP',
  [LOAN_STATES.DEFAULTED]: 'FSP'
};

/**
 * Main Status-to-Message Matrix
 * Maps each loan state to the messages that can be manually triggered
 * Based on ESS API Documentation - Updated December 2024
 */
export const STATUS_MESSAGE_MATRIX = {
  // ========== INITIATION PHASE ==========
  [LOAN_STATES.INITIATED]: [
    'LOAN_STATUS_REQUEST'
  ],

  // ========== FSP PROCESSING PHASE ==========
  [LOAN_STATES.LOAN_OFFER_AT_FSP]: [
    'LOAN_INITIAL_APPROVAL_NOTIFICATION',
    'LOAN_STATUS_REQUEST'
  ],

  [LOAN_STATES.FSP_REJECTED]: [
    'LOAN_STATUS_REQUEST'
  ],

  // ========== EMPLOYEE DECISION PHASE ==========
  [LOAN_STATES.LOAN_OFFER_AT_EMPLOYEE]: [
    'LOAN_STATUS_REQUEST'
  ],

  [LOAN_STATES.EMPLOYEE_REJECTED]: [
    'LOAN_STATUS_REQUEST'
  ],

  // ========== APPROVAL PHASE ==========
  [LOAN_STATES.PENDING_FOR_APPROVAL]: [
    'LOAN_STATUS_REQUEST'
  ],

  [LOAN_STATES.EMPLOYEE_CANCELED]: [
    'LOAN_STATUS_REQUEST'
  ],

  [LOAN_STATES.EMPLOYER_REJECTED]: [
    'LOAN_STATUS_REQUEST'
  ],

  // ========== DISBURSEMENT PHASE ==========
  [LOAN_STATES.SUBMITTED_FOR_DISBURSEMENT]: [
    'LOAN_STATUS_REQUEST',
    'LOAN_DISBURSEMENT_NOTIFICATION',
    'TAKEOVER_DISBURSEMENT_NOTIFICATION'
  ],

  [LOAN_STATES.APPROVED]: [
    'LOAN_STATUS_REQUEST',
    'LOAN_DISBURSEMENT_NOTIFICATION',
    'TAKEOVER_DISBURSEMENT_NOTIFICATION'
  ],

  [LOAN_STATES.FSP_CANCELED]: [
    'LOAN_STATUS_REQUEST'
  ],

  // ========== COMPLETED PHASE ==========
  [LOAN_STATES.COMPLETED]: [
    'LOAN_STATUS_REQUEST',
    'LOAN_LIQUIDATION_NOTIFICATION',
    'LOAN_RESTRUCTURE_REQUEST_FSP',
    'PARTIAL_LOAN_REPAYMENT_NOTIFICATION',
    'FULL_LOAN_REPAYMENT_REQUEST',
    'DEFAULTER_DETAILS_TO_EMPLOYER',
    'FULL_LOAN_REPAYMENT_NOTIFICATION'
  ],

  // ========== TAKEOVER PHASE ==========
  [LOAN_STATES.WAITING_FOR_LIQUIDATION]: [
    'LOAN_STATUS_REQUEST'
  ],

  // ========== FAILURE PHASE ==========
  [LOAN_STATES.DISBURSEMENT_FAILURE]: [
    'LOAN_STATUS_REQUEST'
  ],

  // ========== ACTIVE LOAN PHASE ==========
  [LOAN_STATES.ACTIVE]: [
    'LOAN_STATUS_REQUEST',
    'LOAN_LIQUIDATION_NOTIFICATION',
    'LOAN_RESTRUCTURE_REQUEST_FSP',
    'PARTIAL_LOAN_REPAYMENT_NOTIFICATION',
    'FULL_LOAN_REPAYMENT_REQUEST',
    'DEFAULTER_DETAILS_TO_EMPLOYER',
    'FULL_LOAN_REPAYMENT_NOTIFICATION'
  ],

  [LOAN_STATES.DISBURSED]: [
    'LOAN_STATUS_REQUEST',
    'LOAN_LIQUIDATION_NOTIFICATION',
    'LOAN_RESTRUCTURE_REQUEST_FSP',
    'PARTIAL_LOAN_REPAYMENT_NOTIFICATION',
    'FULL_LOAN_REPAYMENT_REQUEST',
    'DEFAULTER_DETAILS_TO_EMPLOYER',
    'FULL_LOAN_REPAYMENT_NOTIFICATION'
  ],

  // ========== DEFAULT PHASE ==========
  [LOAN_STATES.DEFAULTED]: [
    'LOAN_STATUS_REQUEST',
    'DEFAULTER_DETAILS_TO_EMPLOYER'
  ],

  [LOAN_STATES.OVERDUE]: [
    'LOAN_STATUS_REQUEST',
    'DEFAULTER_DETAILS_TO_EMPLOYER',
    'PARTIAL_LOAN_REPAYMENT_NOTIFICATION'
  ],

  // ========== PENDING (Generic) ==========
  [LOAN_STATES.PENDING]: [
    'LOAN_STATUS_REQUEST'
  ],

  // ========== INTERNAL ESS BACKEND STATUSES ==========
  // These are the actual status values stored in the DB by the ESS backend.
  // They don't appear in the ESS API docs (which use human-readable state names)
  // but must be mapped here so the UI can show suggested messages correctly.

  INITIAL_OFFER: [
    'LOAN_INITIAL_APPROVAL_NOTIFICATION',
    'LOAN_STATUS_REQUEST'
  ],

  OFFER_SUBMITTED: [
    'LOAN_INITIAL_APPROVAL_NOTIFICATION',
    'LOAN_STATUS_REQUEST'
  ],

  INITIAL_APPROVAL_SENT: [
    'LOAN_STATUS_REQUEST'
  ],

  APPROVED: [
    'LOAN_STATUS_REQUEST',
    'LOAN_DISBURSEMENT_NOTIFICATION',
    'TAKEOVER_DISBURSEMENT_NOTIFICATION'
  ],

  FINAL_APPROVAL_RECEIVED: [
    'LOAN_STATUS_REQUEST',
    'LOAN_DISBURSEMENT_NOTIFICATION',
    'TAKEOVER_DISBURSEMENT_NOTIFICATION'
  ],

  CLIENT_CREATED: [
    'LOAN_STATUS_REQUEST'
  ],

  LOAN_CREATED: [
    'LOAN_STATUS_REQUEST',
    'LOAN_DISBURSEMENT_NOTIFICATION',
    'TAKEOVER_DISBURSEMENT_NOTIFICATION'
  ],

  DISBURSED: [
    'LOAN_STATUS_REQUEST',
    'LOAN_LIQUIDATION_NOTIFICATION',
    'LOAN_RESTRUCTURE_REQUEST_FSP',
    'PARTIAL_LOAN_REPAYMENT_NOTIFICATION',
    'FULL_LOAN_REPAYMENT_REQUEST',
    'DEFAULTER_DETAILS_TO_EMPLOYER',
    'FULL_LOAN_REPAYMENT_NOTIFICATION'
  ],

  COMPLETED: [
    'LOAN_STATUS_REQUEST'
  ],

  WAITING_FOR_LIQUIDATION: [
    'LOAN_STATUS_REQUEST',
    'LOAN_LIQUIDATION_NOTIFICATION'
  ],

  DISBURSEMENT_FAILURE_NOTIFICATION_SENT: [
    'LOAN_STATUS_REQUEST',
    'LOAN_DISBURSEMENT_NOTIFICATION'
  ],

  REJECTED: [
    'LOAN_STATUS_REQUEST'
  ],

  CANCELLED: [
    'LOAN_STATUS_REQUEST'
  ],

  FAILED: [
    'LOAN_STATUS_REQUEST'
  ]
};

/**
 * System/Admin Messages - Always available, no loan context required
 * These can be triggered regardless of loan status
 */
export const NO_LOAN_REQUIRED_MESSAGES = [
  'PRODUCT_DETAIL',
  'PRODUCT_DECOMMISSION',
  'PAYMENT_ACKNOWLEDGMENT_NOTIFICATION',
  'FSP_BRANCHES',
  'RESPONSE'
];

// Alias for convenience
export const SYSTEM_MESSAGES = NO_LOAN_REQUIRED_MESSAGES;

/**
 * Check if a message is available for a given status
 * @param {string} status - The loan status
 * @param {string} messageType - The message type to check
 * @returns {boolean}
 */
export const isMessageAvailableForStatus = (status, messageType) => {
  // System messages are always available
  if (SYSTEM_MESSAGES.includes(messageType)) {
    return true;
  }
  
  const allowedMessages = getMessagesForStatus(status);
  return allowedMessages.includes(messageType);
};

/**
 * Normalize status string for comparison
 * @param {string} status - Raw status string
 * @returns {string} - Normalized status
 */
export const normalizeStatus = (status) => {
  if (!status) return '';
  return status.trim().toLowerCase();
};

/**
 * Get allowed messages for a given loan status
 * @param {string} status - The loan status
 * @returns {string[]} - Array of allowed message types
 */
export const getMessagesForStatus = (status) => {
  if (!status) return [];
  
  // Normalize status (handle case variations)
  const normalizedStatus = status.trim();
  
  // Direct match
  if (STATUS_MESSAGE_MATRIX[normalizedStatus]) {
    return STATUS_MESSAGE_MATRIX[normalizedStatus];
  }
  
  // Case-insensitive match
  const matchedKey = Object.keys(STATUS_MESSAGE_MATRIX).find(
    key => key.toLowerCase() === normalizedStatus.toLowerCase()
  );
  
  if (matchedKey) {
    return STATUS_MESSAGE_MATRIX[matchedKey];
  }
  
  // Default - return only status response
  return ['LOAN_STATUS_RESPONSE'];
};

/**
 * Check if a message type is allowed for a given status
 * @param {string} messageType - The message type to check
 * @param {string} status - The loan status
 * @returns {boolean}
 */
export const isMessageAllowedForStatus = (messageType, status) => {
  // System messages are always allowed
  if (NO_LOAN_REQUIRED_MESSAGES.includes(messageType)) {
    return true;
  }
  
  const allowedMessages = getMessagesForStatus(status);
  return allowedMessages.includes(messageType);
};

/**
 * Check if a message requires a loan context
 * @param {string} messageType - The message type
 * @returns {boolean}
 */
export const requiresLoanContext = (messageType) => {
  return !NO_LOAN_REQUIRED_MESSAGES.includes(messageType);
};

/**
 * Get all unique message types from the matrix
 */
export const getAllMessageTypesFromMatrix = () => {
  const allMessages = new Set();
  
  Object.values(STATUS_MESSAGE_MATRIX).forEach(messages => {
    messages.forEach(msg => allMessages.add(msg));
  });
  
  NO_LOAN_REQUIRED_MESSAGES.forEach(msg => allMessages.add(msg));
  
  return Array.from(allMessages);
};

/**
 * Get status color for UI display
 * @param {string} status - The loan status
 * @returns {string} - Color hex code
 */
export const getStatusColor = (status) => {
  const colorMap = {
    [LOAN_STATES.INITIATED]: '#2196f3',        // Blue
    [LOAN_STATES.LOAN_OFFER_AT_FSP]: '#ff9800', // Orange
    [LOAN_STATES.FSP_REJECTED]: '#f44336',      // Red
    [LOAN_STATES.LOAN_OFFER_AT_EMPLOYEE]: '#9c27b0', // Purple
    [LOAN_STATES.EMPLOYEE_REJECTED]: '#f44336', // Red
    [LOAN_STATES.PENDING_FOR_APPROVAL]: '#ff9800', // Orange
    [LOAN_STATES.EMPLOYEE_CANCELED]: '#9e9e9e', // Gray
    [LOAN_STATES.EMPLOYER_REJECTED]: '#f44336', // Red
    [LOAN_STATES.SUBMITTED_FOR_DISBURSEMENT]: '#4caf50', // Green
    [LOAN_STATES.FSP_CANCELED]: '#9e9e9e',      // Gray
    [LOAN_STATES.COMPLETED]: '#4caf50',         // Green
    [LOAN_STATES.WAITING_FOR_LIQUIDATION]: '#ff9800', // Orange
    [LOAN_STATES.DISBURSEMENT_FAILURE]: '#f44336', // Red
    [LOAN_STATES.ACTIVE]: '#4caf50',            // Green
    [LOAN_STATES.DEFAULTED]: '#f44336',         // Red
    [LOAN_STATES.DISBURSED]: '#4caf50',         // Green
    [LOAN_STATES.OVERDUE]: '#ff5722',           // Deep Orange
    [LOAN_STATES.APPROVED]: '#4caf50',          // Green
    [LOAN_STATES.PENDING]: '#ff9800'            // Orange
  };
  
  return colorMap[status] || '#757575';
};

/**
 * Get status icon for UI display
 * @param {string} status - The loan status
 * @returns {string} - Icon name
 */
export const getStatusIcon = (status) => {
  const iconMap = {
    [LOAN_STATES.INITIATED]: 'FileText',
    [LOAN_STATES.LOAN_OFFER_AT_FSP]: 'Send',
    [LOAN_STATES.FSP_REJECTED]: 'XCircle',
    [LOAN_STATES.LOAN_OFFER_AT_EMPLOYEE]: 'FileCheck',
    [LOAN_STATES.EMPLOYEE_REJECTED]: 'XCircle',
    [LOAN_STATES.PENDING_FOR_APPROVAL]: 'Clock',
    [LOAN_STATES.EMPLOYEE_CANCELED]: 'X',
    [LOAN_STATES.EMPLOYER_REJECTED]: 'XCircle',
    [LOAN_STATES.SUBMITTED_FOR_DISBURSEMENT]: 'DollarSign',
    [LOAN_STATES.FSP_CANCELED]: 'X',
    [LOAN_STATES.COMPLETED]: 'CheckCircle',
    [LOAN_STATES.WAITING_FOR_LIQUIDATION]: 'Hourglass',
    [LOAN_STATES.DISBURSEMENT_FAILURE]: 'AlertTriangle',
    [LOAN_STATES.ACTIVE]: 'Activity',
    [LOAN_STATES.DEFAULTED]: 'AlertCircle',
    [LOAN_STATES.DISBURSED]: 'CheckCircle',
    [LOAN_STATES.OVERDUE]: 'AlertTriangle',
    [LOAN_STATES.APPROVED]: 'CheckCircle',
    [LOAN_STATES.PENDING]: 'Clock'
  };
  
  return iconMap[status] || 'Circle';
};

export default STATUS_MESSAGE_MATRIX;
